import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart,  } from 'recharts';

const IRR_TABLE = [
  { exitYear: 3, bear: 8.1, base: 11.4, bull: 14.2 },
  { exitYear: 5, bear: 9.8, base: 13.2, bull: 16.5 },
  { exitYear: 7, bear: 11.2, base: 14.2, bull: 18.1 },
  { exitYear: 10, bear: 10.5, base: 13.8, bull: 17.4 },
];

const NAV_DATA = Array.from({ length: 11 }, (_, i) => ({
  year: i,
  base: Math.round(100 * Math.pow(1.142, i)),
  bear: Math.round(100 * Math.pow(1.098, i)),
  bull: Math.round(100 * Math.pow(1.178, i)),
}));

const MOIC = [
  { label: 'Bear', value: '1.4x', color: 'text-red-600', bg: 'bg-red-50' },
  { label: 'Base', value: '1.9x', color: 'text-amber-600', bg: 'bg-amber-50' },
  { label: 'Bull', value: '2.4x', color: 'text-emerald-600', bg: 'bg-emerald-50' },
];

function IRRCell({ value, target }) {
  const diff = value - target;
  let cls = 'text-red-600 bg-red-50';
  if (diff >= 0) cls = 'text-emerald-700 bg-emerald-50';
  else if (diff >= -2) cls = 'text-amber-700 bg-amber-50';
  return (
    <td className={`px-3 py-2 text-center text-sm font-semibold rounded ${cls}`}>{value}%</td>
  );
}

export default function WaterfallReturnsTab({ deal, readOnly }) {
  const [hurdleRate, setHurdleRate] = useState(deal?.hurdleRate || 8);
  const [carry, setCarry] = useState(deal?.carry || 20);
  const [gpCommitment, setGpCommitment] = useState(2);
  const [clawback, setClawback] = useState(true);
  const [managementFee, setManagementFee] = useState(1.5);
  const [waterfallType, setWaterfallType] = useState('European');

  const waterfallSteps = [
    { id: 1, name: 'Return of Capital', threshold: 'Capital return', thresholdValue: '1.0x MOIC', investorSplit: 100, gpSplit: 0, notes: 'All capital returned to investors first' },
    { id: 2, name: 'Preferred Return', threshold: 'IRR hurdle', thresholdValue: `${hurdleRate}% p.a.`, investorSplit: 100, gpSplit: 0, notes: 'Preferred return accrued on unreturned capital' },
    { id: 3, name: 'GP Catch-up', threshold: 'Multiple', thresholdValue: 'Until GP receives carry %', investorSplit: 20, gpSplit: 80, notes: 'GP catch-up until carry threshold reached' },
    { id: 4, name: 'Carried Interest Split', threshold: 'Residual', thresholdValue: 'Remaining distributions', investorSplit: 100 - carry, gpSplit: carry, notes: 'Final split of residual profits' },
  ];

  const targetIRR = deal?.targetIRR || 14.2;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Waterfall Cascade */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="GitBranch" size={16} color="var(--color-primary)" />
            Distribution Waterfall
          </h3>
          <div className="space-y-2">
            {waterfallSteps?.map((step, idx) => (
              <div key={step?.id}>
                <div className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/20">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">{step?.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{step?.notes}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs bg-[rgba(59,76,76,0.07)] text-blue-700 px-2 py-0.5 rounded-full font-medium">
                        Investors: {step?.investorSplit}%
                      </span>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                        GP: {step?.gpSplit}%
                      </span>
                      <span className="text-xs text-muted-foreground">{step?.thresholdValue}</span>
                    </div>
                  </div>
                </div>
                {idx < waterfallSteps?.length - 1 && (
                  <div className="flex justify-center my-1">
                    <Icon name="ChevronDown" size={16} color="var(--color-muted-foreground)" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Settings */}
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Settings" size={16} color="var(--color-primary)" />
            Hurdle Rate & Carry Settings
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Hurdle Rate (%)</label>
                <input type="number" value={hurdleRate} onChange={(e) => !readOnly && setHurdleRate(parseFloat(e?.target?.value) || 0)} disabled={readOnly}
                  className="w-full mt-1 px-3 py-1.5 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Carried Interest (%)</label>
                <input type="number" value={carry} onChange={(e) => !readOnly && setCarry(parseFloat(e?.target?.value) || 0)} disabled={readOnly}
                  className="w-full mt-1 px-3 py-1.5 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">GP Commitment (%)</label>
                <input type="number" value={gpCommitment} onChange={(e) => !readOnly && setGpCommitment(parseFloat(e?.target?.value) || 0)} disabled={readOnly}
                  className="w-full mt-1 px-3 py-1.5 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Management Fee (% p.a.)</label>
                <input type="number" value={managementFee} onChange={(e) => !readOnly && setManagementFee(parseFloat(e?.target?.value) || 0)} disabled={readOnly}
                  className="w-full mt-1 px-3 py-1.5 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Waterfall Type</label>
              <div className="flex gap-2 mt-1">
                {['European', 'American']?.map((wt) => (
                  <button key={wt} onClick={() => !readOnly && setWaterfallType(wt)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      waterfallType === wt ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-foreground border-border hover:bg-muted'
                    } ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    {wt}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm text-foreground">Clawback Provision</label>
              <button onClick={() => !readOnly && setClawback((v) => !v)} disabled={readOnly}
                className={`relative w-10 h-5 rounded-full transition-colors ${clawback ? 'bg-primary' : 'bg-muted'} disabled:opacity-60`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${clawback ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* IRR Sensitivity Table */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Table" size={16} color="var(--color-primary)" />
          IRR Sensitivity Analysis
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">Exit Year</th>
                <th className="px-3 py-2 text-center text-xs font-semibold text-red-600">Bear (−10% EBITDA)</th>
                <th className="px-3 py-2 text-center text-xs font-semibold text-amber-600">Base Case</th>
                <th className="px-3 py-2 text-center text-xs font-semibold text-emerald-600">Bull (+15% EBITDA)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {IRR_TABLE?.map((row) => (
                <tr key={row?.exitYear}>
                  <td className="px-3 py-2 font-medium text-foreground">Year {row?.exitYear}</td>
                  <IRRCell value={row?.bear} target={targetIRR} />
                  <IRRCell value={row?.base} target={targetIRR} />
                  <IRRCell value={row?.bull} target={targetIRR} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Green = at or above target IRR ({targetIRR}%) · Amber = within 2pts · Red = below target</p>
      </div>
      {/* NAV Projection + MOIC */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="TrendingUp" size={16} color="var(--color-primary)" />
            NAV Projection (10-Year)
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={NAV_DATA} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} label={{ value: 'Year', position: 'insideBottom', offset: -2, fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} unit="" />
              <Tooltip formatter={(v) => `${v} (indexed)`} />
              <Legend />
              <Area type="monotone" dataKey="bear" name="Bear" stroke="#ef4444" fill="#fee2e2" strokeWidth={1.5} />
              <Area type="monotone" dataKey="base" name="Base" stroke="#1e40af" fill="#dbeafe" strokeWidth={2} />
              <Area type="monotone" dataKey="bull" name="Bull" stroke="#059669" fill="#d1fae5" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-semibold text-foreground mb-4">MOIC Summary</h3>
          <div className="space-y-3">
            {MOIC?.map((m) => (
              <div key={m?.label} className={`${m?.bg} rounded-lg p-4 text-center`}>
                <p className="text-xs text-muted-foreground">{m?.label} Case</p>
                <p className={`text-2xl font-bold ${m?.color}`}>{m?.value}</p>
                <p className="text-xs text-muted-foreground">Money-on-Invested-Capital</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
